import { FetchError } from '@/connection/helpers';

/**
 * Serialized representation of an error, safe for Redux store.
 */
export interface SerializedError {
  message: string;
  name?: string;
  stack?: string;
  /** HTTP status code, present when the original error was a FetchError. */
  status?: number;
  cause?: unknown;
}

/**
 * Serializes an unknown caught error into a plain object safe for Redux store.
 * Error instances are not serializable and will crash the Redux serializable check middleware.
 */
export const serializeError = (error: unknown): SerializedError => {
  if (error instanceof FetchError) {
    return {
      message: error.message,
      name: 'FetchError',
      stack: error.stack,
      status: error.response?.status,
      cause: error?.cause,
    };
  }
  if (error instanceof Error) {
    return {
      message: error.message,
      name: error.name,
      stack: error.stack,
      cause: error.cause,
    };
  }
  if (typeof error === 'object' && error !== null) {
    return { message: JSON.stringify(error) };
  }
  return { message: String(error) };
};

/**
 * Extracts a message string from an asyncThunk rejected action payload,
 * which may be a SerializedError object or a plain string.
 */
export const getPayloadMessage = (payload: SerializedError | string | undefined): string | undefined => {
  if (!payload) return undefined;
  return typeof payload === 'string' ? payload : payload.message;
};
